import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, LayoutDashboard, GraduationCap, FileText, Calendar, Users, Settings, Brain, Target } from 'lucide-react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { useCollegeContext } from '../../contexts/CollegeContext';
import { supabase } from '../../lib/supabase';
import CollegeJourneyHubV2 from './CollegeJourneyHubV2';
import EnhancedCollegeDataHub from './EnhancedCollegeDataHub';
import UnifiedEssayHub from './EssayDevelopmentHub/UnifiedEssayHub';
// import EssayDevelopmentHub from './EssayDevelopmentHub/EssayDevelopmentHub'; // Legacy - replaced by UnifiedEssayHub
// import MasterTimeline from './MasterTimeline'; // Old timeline - backed up to archived/MasterTimeline.backup.jsx
// import UnifiedTimelineIntegrated from './UnifiedTimelineIntegrated'; // Moved to _archived/UnifiedTimelineIntegrated.jsx
import WorkflowTimelinePage from '../../pages/WorkflowTimelinePage';
import ParentCollegeHub from './ParentCollegeHub';
import StudentProfileHub from './StudentProfileHub';
import StrategicAssessmentHub from './StrategicAssessmentHub';
import CollegeJourneyNavigation from './CollegeJourneyNavigation';
import FinancialAidHub from './FinancialAidHub';
import CollegeComparisonHub from './ComparisonHub/CollegeComparisonHub';

const CollegeJourneyPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useFamilyContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [studentProfile, setStudentProfile] = useState(null);
  const [collegeList, setCollegeList] = useState([]);
  const [essaySlots, setEssaySlots] = useState([]);

  console.log('🔍 CollegeJourneyPage: Component rendered with currentUser:', currentUser);
  console.log('🔍 CollegeJourneyPage: Current collegeList state:', collegeList);
  
  // Determine active tab from URL path or query params
  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes('/college/dashboard')) return 'dashboard';
    if (path.includes('/college/profile')) return 'profile';
    // Check for comparison-related sub-routes under /college/colleges first
    if (path.includes('/college/colleges/compare')) return 'compare';
    if (path.includes('/college/colleges/tier-analysis')) return 'compare';
    if (path.includes('/college/colleges/portfolio-strategy')) return 'compare';
    if (path.includes('/college/colleges')) return 'colleges';
    if (path.includes('/college/essays')) return 'essays';
    if (path.includes('/college/timeline')) return 'timeline';
    if (path.includes('/college/financial-aid')) return 'financial-aid';
    if (path.includes('/college/compare')) return 'compare';
    return searchParams.get('tab') || 'dashboard';
  };
  
  // Check if user is a parent
  const isParent = currentUser?.isParent || currentUser?.type === 'parent';

  const activeTab = getActiveTab();

  useEffect(() => {
    console.log('🔍 CollegeJourneyPage: useEffect triggered with currentUser:', currentUser?.id);

    const loadStudentData = async () => {
      if (!currentUser) {
        console.log('🔍 CollegeJourneyPage: No currentUser, skipping data load');
        return;
      }

      console.log('🔍 CollegeJourneyPage: Loading student data for user:', currentUser);

      try {
        const selectedStudentUserId = localStorage.getItem('college-selected-student-id');
        const userIdForQuery = selectedStudentUserId || currentUser.id;

        console.log('🔍 CollegeJourneyPage: Using user ID for query:', userIdForQuery);

        console.log('🔍 CollegeJourneyPage: About to query student_academic_profiles...');
        const { data: profileData, error: profileError } = await supabase
          .from('student_academic_profiles')
          .select('*')
          .eq('user_id', userIdForQuery)
          .single();

        console.log('🔍 CollegeJourneyPage: Profile query result:', { profileData, profileError });

        if (profileData) {
          setStudentProfile(profileData);
        } else if (profileError) {
          console.error('🔍 CollegeJourneyPage: Profile query error:', profileError);
        }

        console.log('🔍 CollegeJourneyPage: About to query college_journey_colleges...');
        // Fetch from college_journey_colleges table with join to global_colleges
        const { data: colleges, error: collegeError } = await supabase
          .from('college_journey_colleges')
          .select(`
            *,
            global_colleges (
              id,
              name,
              location,
              acceptance_rate,
              cs_ranking,
              usnwr_ranking,
              business_ranking,
              liberal_arts_ranking
            )
          `)
          .eq('user_id', userIdForQuery)
          .is('removed_at', null)  // Only get active colleges
          .order('priority_level', { ascending: true });

        console.log('🔍 CollegeJourneyPage: College query result:', { colleges, collegeError });
        console.log('🔍 CollegeJourneyPage: College count:', colleges?.length || 0);

        if (colleges) {
          setCollegeList(colleges);
          console.log('🔍 CollegeJourneyPage: Set college list with', colleges.length, 'colleges');
        } else if (collegeError) {
          console.error('🔍 CollegeJourneyPage: College query error:', collegeError);
        } else {
          console.log('🔍 CollegeJourneyPage: No colleges returned from query');
        }

        // Fetch essay slots for this user
        console.log('🔍 CollegeJourneyPage: Fetching essay slots...');
        const { data: slots, error: slotsError } = await supabase
          .from('essay_slots')
          .select(`
            *,
            college_journey_colleges (
              id,
              name,
              global_colleges (
                name
              )
            )
          `)
          .eq('user_id', userIdForQuery)
          .not('prompt_text', 'is', null); // Only get slots with prompts

        console.log('🔍 CollegeJourneyPage: Essay slots result:', { slots, slotsError });

        if (slots) {
          setEssaySlots(slots);
          console.log('🔍 CollegeJourneyPage: Set essay slots:', slots.length, 'slots with prompts');
        }
      } catch (error) {
        console.error('🔍 CollegeJourneyPage: Error loading student data:', error);
      }
    };

    loadStudentData();
  }, [currentUser]);

  // Handle tab change
  const handleTabChange = (tab) => {
    // Both parents and students now use the same URL routing
    if (tab === 'dashboard') {
      navigate('/college');
    } else {
      navigate(`/college/${tab}`);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <CollegeJourneyNavigation customTabHandler={handleTabChange} />

      {/* Content */}
      <div className="px-4 py-4 space-y-4 pb-20">
        {activeTab === 'dashboard' ? (
          /* Dashboard Tab - Different content for parents vs students */
          isParent ? <ParentCollegeHub /> : <CollegeJourneyHubV2 />
        ) : activeTab === 'profile' ? (
          /* Profile Tab - Student profile hub */
          <StudentProfileHub />
        ) : activeTab === 'strategic-assessment' ? (
          /* Strategic Assessment Tab - Assessment overview, insights, and settings */
          <StrategicAssessmentHub />
        ) : activeTab === 'colleges' ? (
          /* Colleges Tab - Same for both parents and students */
          <EnhancedCollegeDataHub />
        ) : activeTab === 'essays' ? (
          /* Essays Tab - New unified essay hub with Free Writing, Ideation, Workspace, and Slots */
          <UnifiedEssayHub />
        ) : activeTab === 'financial-aid' ? (
          /* Financial Aid Tab - Same for both parents and students */
          <FinancialAidHub />
        ) : activeTab === 'compare' ? (
          /* College Comparison Tab - Side-by-side comparison */
          <CollegeComparisonHub />
        ) : (
          /* Timeline Tab - Same for both parents and students */
          <WorkflowTimelinePage />
        )}
      </div>
    </div>
  );
};

export default CollegeJourneyPage;